import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Toaster, toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  Home,
  ChevronRight,
  Copy,
  Share2,
  TrendingUp,
  UserPlus,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Component
const SidebarNav = ({ isOpen, setIsOpen }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  const navItems = [
    { icon: LayoutDashboard, label: 'Overview', path: '/dashboard' },
    { icon: Sparkles, label: 'AI Estimate', path: '/dashboard/ai-estimate', isNew: true },
    { icon: FileText, label: 'Cost Estimates', path: '/dashboard/estimates' },
    { icon: Users, label: 'Referrals', path: '/dashboard/referrals' },
    { icon: Building2, label: 'Business Network', path: '/dashboard/businesses' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-100 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-semibold text-primary text-sm">MED CONNECT</span>
                <span className="text-accent font-light ml-1 text-sm">ALBANIA</span>
              </div>
            </Link>
          </div>
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <span className="text-accent font-semibold">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Menu</p>
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(item.path) ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:bg-slate-50 hover:text-primary'}`}>
                <item.icon className="w-5 h-5" />{item.label}
                {item.isNew && <Badge className="ml-auto bg-accent text-white text-xs">NEW</Badge>}
              </Link>
            ))}
            {isAdmin && (
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Admin</p>
                <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary">
                  <Settings className="w-5 h-5" />Admin Panel
                </Link>
              </div>
            )}
          </nav>
          <div className="p-4 border-t border-slate-100 space-y-2">
            <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary">
              <Home className="w-5 h-5" />Back to Home
            </Link>
            <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full">
              <LogOut className="w-5 h-5" />Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Referrals Content
const ReferralsContent = () => {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [referralsRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/referrals`, { credentials: 'include' }),
          fetch(`${API_URL}/api/referrals/stats`, { credentials: 'include' })
        ]);
        
        if (referralsRes.ok) setReferrals(await referralsRes.json());
        if (statsRes.ok) setStats(await statsRes.json());
      } catch (error) {
        console.error('Failed to fetch referrals:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const copyReferralLink = async () => {
    const link = `${window.location.origin}/register?ref=${user?.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Referral link copied!');
    }
  };

  const shareReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${user?.referral_code}`;
    const text = `Join MED CONNECT ALBANIA for premium medical treatments at affordable prices! Use my referral link: ${link}`;
    
    if (navigator.share) {
      navigator.share({ title: 'MED CONNECT ALBANIA', text, url: link });
    } else {
      copyReferralLink();
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <h1 className="text-2xl font-semibold text-primary">Referral Program</h1>
        <p className="text-muted-foreground">Track your referrals and earn commissions.</p>
      </div>

      {/* Referral Link Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-primary to-primary/90 rounded-xl p-6 text-white"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold mb-2">Your Referral Code</h3>
            <p className="text-3xl font-mono font-bold mb-2">{user?.referral_code || 'Loading...'}</p>
            <p className="text-white/80 text-sm">Share this code to earn 5-10% commission on each referral.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={copyReferralLink} data-testid="copy-code-btn">
              <Copy className="w-4 h-4 mr-2" />Copy Link
            </Button>
            <Button variant="secondary" size="sm" onClick={shareReferralLink} data-testid="share-btn">
              <Share2 className="w-4 h-4 mr-2" />Share
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Total Referrals</p>
            <UserPlus className="w-5 h-5 text-accent" />
          </div>
          <p className="text-3xl font-bold text-primary">{stats?.total_referrals || 0}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Pending</p>
            <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
          </div>
          <p className="text-3xl font-bold text-yellow-500">{stats?.pending || 0}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Completed</p>
            <div className="w-2 h-2 rounded-full bg-green-400"></div>
          </div>
          <p className="text-3xl font-bold text-green-500">{stats?.completed || 0}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Total Commission</p>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-accent">€{(stats?.total_commission || 0).toFixed(2)}</p>
        </motion.div>
      </div>

      {/* Referrals List */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-semibold text-primary">Your Referrals</h3>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No referrals yet</h3>
            <p className="text-muted-foreground mb-4">Share your referral link to start earning commissions.</p>
            <Button onClick={copyReferralLink}>
              <Copy className="w-4 h-4 mr-2" />Copy Referral Link
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Commission</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {referrals.map((referral) => (
                  <tr key={referral.referral_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium">{referral.referred_user_name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{referral.referred_user_email}</td>
                    <td className="px-4 py-3">{getStatusBadge(referral.status)}</td>
                    <td className="px-4 py-3 text-sm">
                      {referral.commission_amount 
                        ? <span className="text-green-600 font-medium">€{referral.commission_amount.toFixed(2)}</span>
                        : <span className="text-muted-foreground">{referral.commission_rate}%</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(referral.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Referrals Page
export default function ReferralsPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />
      <SidebarNav isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100 lg:pl-64">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <div className="flex items-center gap-4">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2 text-sm">
              <Link to="/dashboard" className="text-muted-foreground hover:text-primary">Dashboard</Link>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Referrals</span>
            </div>
          </div>
        </div>
      </header>
      
      <main className="lg:pl-64 pt-6 pb-12 px-4 md:px-8">
        <ReferralsContent />
      </main>
    </div>
  );
}
